import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCity } from '../features/city/useCity';
import { deviceStorage, type KeyValueStorage } from './storage';

/**
 * Events the user marked "Not Interested", kept off Explore.
 *
 * localStorage, per city: `cg.hidden.<city>`. With accounts this list would
 * belong to the account; see handoff/hidden-and-edited-events.md.
 */

const keyFor = (city: string) => `cg.hidden.${city}`;

function read(storage: KeyValueStorage, key: string): string[] {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(key) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export interface HiddenEvents {
  ids: Set<string>;
  isHidden: (id: string) => boolean;
  hide: (id: string) => void;
  unhide: (id: string) => void;
}

export function useHiddenEvents(): HiddenEvents {
  const storage = deviceStorage;
  const key = keyFor(useCity().city);
  const [list, setList] = useState<string[]>(() => read(storage, key));

  // Another city: its own list.
  const [source, setSource] = useState(key);
  if (source !== key) {
    setSource(key);
    setList(read(storage, key));
  }

  useEffect(() => {
    try {
      storage.setItem(source, JSON.stringify(list));
    } catch {
      // Site data blocked. Hiding still works for this session.
    }
  }, [list, storage, source]);

  const ids = useMemo(() => new Set(list), [list]);
  const hide = useCallback((id: string) => setList((l) => (l.includes(id) ? l : [...l, id])), []);
  const unhide = useCallback((id: string) => setList((l) => l.filter((x) => x !== id)), []);

  return useMemo(
    () => ({ ids, isHidden: (id: string) => ids.has(id), hide, unhide }),
    [ids, hide, unhide],
  );
}
